import posthog from 'posthog-js';

export const posthogProjectToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN;
export const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
export const isPostHogConfigured = Boolean(posthogProjectToken && posthogHost);

export interface CaptureExceptionOptions {
  // React component stack from componentDidCatch, attached as the error cause.
  componentStack?: string;
  // False when the crash broke the UI and the app could not recover.
  handled?: boolean;
}

// Redact values that can carry personal data or secrets, and keep the rest of
// the text so error tracking gives each failure a distinct, searchable message.
const scrubSensitiveValues = (text: string): string =>
  text
    .replace(/[^\s"'<>()@]+@[^\s"'<>()@]+\.[^\s"'<>()@]+/g, '[redacted-email]')
    .replace(/eyJ[\w-]+\.[\w-]+\.[\w-]*/g, '[redacted-token]')
    .replace(/\b(Bearer|Basic)\s+[\w.\-+/=]+/gi, '$1 [redacted-token]');

const makeError = (message: string, name?: string, stack?: string): Error => {
  const error = new Error(message);
  if (name) error.name = name;
  if (stack) error.stack = stack;
  return error;
};

const scrubError = (error: Error): Error =>
  makeError(
    scrubSensitiveValues(error.message),
    error.name,
    error.stack ? scrubSensitiveValues(error.stack) : undefined
  );

// Mirrors the cause error that @posthog/react attaches, so the React component
// stack rides along as a second entry in the exception list.
const componentStackError = (error: Error, componentStack: string): Error =>
  makeError(error.message, `React ErrorBoundary ${error.name}`, componentStack);

export const captureSanitizedException = (
  operation: string,
  error: unknown,
  options: CaptureExceptionOptions = {}
): void => {
  if (!isPostHogConfigured) return;

  const sanitizedError =
    error instanceof Error
      ? scrubError(error)
      : makeError(`${operation}: ${scrubSensitiveValues(String(error))}`);

  if (options.componentStack) {
    sanitizedError.cause = componentStackError(sanitizedError, options.componentStack);
  }

  posthog.captureException(sanitizedError, {
    operation,
    handled: options.handled ?? true,
  });
};
